import { useEffect, useState } from 'react';
import api, { errMsg, branchLabel } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { UserPlus, KeyRound, Trash2, Crown, Loader2, Store, Pencil } from 'lucide-react';
import { BranchBadge } from '../components/Layout';

export default function Pengguna() {
  const { user: me, branches, refreshBranches } = useAuth();
  const [users, setUsers] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [pinUser, setPinUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [form, setForm] = useState({ name: '', role: 'kasir', branch: branches[0]?.id || 'balaraja', pin: '' });
  const [newPin, setNewPin] = useState('');
  const [saving, setSaving] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [newUserBranch, setNewUserBranch] = useState('');
  const [branchEdit, setBranchEdit] = useState(null);

  const load = () => api.get('/users').then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Nama wajib diisi');
    if (!/^\d{4,6}$/.test(form.pin)) return toast.error('PIN harus 4-6 digit angka');
    setSaving(true);
    try {
      let branchId = form.branch;
      if (form.role === 'kasir' && newUserBranch.trim()) {
        const { data: nb } = await api.post('/branches', { name: newUserBranch.trim() });
        branchId = nb.id;
        refreshBranches().catch(() => {});
      }
      await api.post('/users', { ...form, branch: branchId });
      toast.success(`User ${form.name} berhasil dibuat`);
      setAddOpen(false);
      setForm({ name: '', role: 'kasir', branch: branches[0]?.id || 'balaraja', pin: '' });
      setNewUserBranch('');
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const addBranch = async () => {
    if (!newBranchName.trim()) return toast.error('Isi nama cabang');
    try {
      await api.post('/branches', { name: newBranchName.trim() });
      toast.success(`Cabang ${newBranchName.trim()} ditambahkan`);
      setNewBranchName('');
      refreshBranches().catch(() => {});
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveBranchName = async () => {
    if (!branchEdit?.name.trim()) return toast.error('Nama cabang tidak boleh kosong');
    try {
      await api.put(`/branches/${branchEdit.id}`, { name: branchEdit.name.trim() });
      toast.success('Nama cabang berhasil diubah — tampilan di semua menu ikut berubah');
      setBranchEdit(null);
      refreshBranches().catch(() => {});
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const submitPin = async () => {
    if (!/^\d{4,6}$/.test(newPin)) return toast.error('PIN harus 4-6 digit angka');
    setSaving(true);
    try {
      await api.put(`/users/${pinUser.id}/pin`, { pin: newPin });
      toast.success(`PIN ${pinUser.name} berhasil diubah`);
      setPinUser(null);
      setNewPin('');
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${deleteUser.id}`);
      toast.success(`User ${deleteUser.name} dihapus`);
      setDeleteUser(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="pengguna-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Manajemen Pengguna</h2>
          <p className="text-sm text-muted-foreground">Tambah user, atur akses full/terbatas, ubah PIN, hapus user</p>
        </div>
        <button onClick={() => setAddOpen(true)} data-testid="add-user-button"
          className="px-4 py-2.5 rounded-xl bg-brand hover:bg-branddark text-white text-sm font-bold flex items-center gap-2 shadow-lg shadow-brand/25 transition-all active:scale-[0.98]">
          <UserPlus className="w-4 h-4" /> Tambah User
        </button>
      </div>

      <div className="lodi-card p-5" data-testid="branch-management">
        <h3 className="font-display font-bold mb-1 flex items-center gap-2"><Store className="w-5 h-5 text-brand" /> Cabang Toko</h3>
        <p className="text-xs text-muted-foreground mb-4">Ubah nama cabang jika toko pindah lokasi (misal Balaraja → nama baru), atau tambah cabang baru. Stok & riwayat lama tetap aman.</p>
        <div className="space-y-2 mb-3">
          {branches.map((b) => (
            <div key={b.id} className="flex items-center gap-2 bg-elevated rounded-lg px-3 py-2" data-testid={`branch-row-${b.id}`}>
              {branchEdit?.id === b.id ? (
                <>
                  <input value={branchEdit.name} onChange={(e) => setBranchEdit({ ...branchEdit, name: e.target.value })} className="lodi-input" data-testid={`branch-rename-input-${b.id}`} />
                  <button onClick={saveBranchName} data-testid={`branch-rename-save-${b.id}`}
                    className="shrink-0 px-3 py-2 rounded-lg bg-brand hover:bg-branddark text-white text-xs font-bold transition-colors">Simpan</button>
                  <button onClick={() => setBranchEdit(null)} data-testid={`branch-rename-cancel-${b.id}`}
                    className="shrink-0 px-3 py-2 rounded-lg bg-surface border border-line text-xs font-semibold">Batal</button>
                </>
              ) : (
                <>
                  <span className="flex-1 font-semibold text-sm">{b.name}</span>
                  <button onClick={() => setBranchEdit({ id: b.id, name: b.name })} data-testid={`branch-edit-${b.id}`} title="Ubah nama cabang"
                    className="w-8 h-8 rounded-lg bg-surface border border-line flex items-center justify-center text-muted-foreground hover:text-brand hover:border-brand/40 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={newBranchName} onChange={(e) => setNewBranchName(e.target.value)} placeholder="Nama cabang baru, cth: Cikupa..." className="lodi-input" data-testid="branch-new-input" />
          <button onClick={addBranch} data-testid="branch-add-button"
            className="shrink-0 px-4 rounded-lg bg-brand hover:bg-branddark text-white text-sm font-bold transition-colors active:scale-[0.98]">Tambah</button>
        </div>
      </div>

      {!users ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="user-list">
          {users.map((u) => (
            <div key={u.id} className="lodi-card p-5" data-testid={`user-card-${u.id}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-display font-bold text-lg ${
                  u.role === 'admin' ? 'bg-brand/15 text-brand' : u.branch === 'balaraja' ? 'bg-blue-500/15 text-blue-400' : u.branch === 'ciledug' ? 'bg-purple-500/15 text-purple-400' : 'bg-teal-500/15 text-teal-400'
                }`}>
                  {u.role === 'admin' ? <Crown className="w-6 h-6" /> : u.name.charAt(0)}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold truncate">{u.name} {u.id === me.id && <span className="text-xs text-muted-foreground">(Anda)</span>}</p>
                  <p className="text-xs text-muted-foreground">{u.role === 'admin' ? 'Admin — Full Akses' : 'Kasir — Akses Terbatas'}</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <BranchBadge branch={u.branch} />
                <div className="flex gap-1.5">
                  <button onClick={() => { setPinUser(u); setNewPin(''); }} data-testid={`change-pin-${u.id}`} title="Ubah PIN"
                    className="w-8 h-8 rounded-lg bg-elevated border border-line flex items-center justify-center text-muted-foreground hover:text-amber-400 hover:border-amber-500/40 transition-colors">
                    <KeyRound className="w-4 h-4" />
                  </button>
                  <button onClick={() => setDeleteUser(u)} disabled={u.id === me.id} data-testid={`delete-user-${u.id}`} title="Hapus user"
                    className="w-8 h-8 rounded-lg bg-elevated border border-line flex items-center justify-center text-muted-foreground hover:text-brand hover:border-brand/40 transition-colors disabled:opacity-30">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-surface border-line max-w-sm" data-testid="add-user-dialog">
          <DialogHeader><DialogTitle className="font-display">Tambah User Baru</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <span className="lodi-label">Nama *</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="lodi-input" placeholder="cth: Kasir Balaraja 2" data-testid="user-name-input" />
            </div>
            <div>
              <span className="lodi-label">Akses</span>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setForm({ ...form, role: 'kasir' })} data-testid="role-kasir-button"
                  className={`px-3 py-2.5 rounded-lg text-sm font-bold border ${form.role === 'kasir' ? 'bg-brand text-white border-brand' : 'bg-elevated border-line text-muted-foreground'}`}>
                  Terbatas (Kasir)
                </button>
                <button onClick={() => setForm({ ...form, role: 'admin' })} data-testid="role-admin-button"
                  className={`px-3 py-2.5 rounded-lg text-sm font-bold border ${form.role === 'admin' ? 'bg-brand text-white border-brand' : 'bg-elevated border-line text-muted-foreground'}`}>
                  Full (Admin)
                </button>
              </div>
            </div>
            {form.role === 'kasir' && (
              <div>
                <span className="lodi-label">Cabang</span>
                <div className="flex flex-wrap gap-2">
                  {branches.map((b) => (
                    <button key={b.id} onClick={() => { setForm({ ...form, branch: b.id }); setNewUserBranch(''); }} data-testid={`user-branch-${b.id}`}
                      className={`px-3 py-2.5 rounded-lg text-sm font-bold border ${form.branch === b.id && !newUserBranch.trim() ? 'bg-brand text-white border-brand' : 'bg-elevated border-line text-muted-foreground'}`}>
                      {b.name}
                    </button>
                  ))}
                </div>
                <input value={newUserBranch} onChange={(e) => setNewUserBranch(e.target.value)} placeholder="Atau ketik nama cabang baru..." className="lodi-input mt-2" data-testid="user-new-branch-input" />
                {newUserBranch.trim() && <p className="text-xs text-emerald-400 mt-1">Cabang baru &quot;{newUserBranch.trim()}&quot; akan dibuat otomatis</p>}
              </div>
            )}
            <div>
              <span className="lodi-label">PIN (4-6 digit) *</span>
              <input type="password" inputMode="numeric" maxLength={6} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '') })} className="lodi-input font-mono tracking-widest" placeholder="••••" data-testid="user-pin-input" />
            </div>
            <button onClick={submit} disabled={saving} data-testid="submit-user-button"
              className="w-full py-3 rounded-xl bg-brand hover:bg-branddark disabled:opacity-40 text-white font-display font-bold transition-all active:scale-[0.98]">
              {saving ? 'MENYIMPAN...' : 'SIMPAN USER'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pinUser} onOpenChange={(v) => !v && setPinUser(null)}>
        <DialogContent className="bg-surface border-line max-w-xs" data-testid="change-pin-dialog">
          <DialogHeader><DialogTitle className="font-display">Ubah PIN — {pinUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input type="password" inputMode="numeric" maxLength={6} value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
              className="lodi-input font-mono tracking-widest text-center text-xl" placeholder="PIN baru" data-testid="new-pin-input" />
            <button onClick={submitPin} disabled={saving} data-testid="submit-pin-button"
              className="w-full py-3 rounded-xl bg-brand hover:bg-branddark disabled:opacity-40 text-white font-display font-bold transition-all active:scale-[0.98]">
              {saving ? 'MENYIMPAN...' : 'SIMPAN PIN BARU'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteUser} onOpenChange={(v) => !v && setDeleteUser(null)}>
        <AlertDialogContent className="bg-surface border-line" data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Hapus User?</AlertDialogTitle>
            <AlertDialogDescription>{deleteUser?.name} tidak akan bisa login lagi. Tindakan ini tidak bisa dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-elevated border-line" data-testid="delete-user-abort">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-brand hover:bg-branddark text-white" data-testid="delete-user-confirm">Ya, Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
